import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Z_INDEX } from '../../constants/zIndex';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'auto';
export type TooltipAlign = 'start' | 'center' | 'end' | 'auto';

export interface TooltipProps {
	children: ReactNode;
	content: ReactNode;
	position?: TooltipPosition;
	align?: TooltipAlign;
	showArrow?: boolean;
	className?: string;
	contentClassName?: string;
	maxWidth?: string;
	disabled?: boolean;
	visible?: boolean;
	onVisibilityChange?: (visible: boolean) => void;
	portal?: boolean;
	zIndex?: number;
	/**
	 * Milliseconds to wait after mouseenter before showing the tooltip.
	 * Cancelled if the user moves off the trigger before the timer fires.
	 * Default 0 (show immediately) — matches prior behavior for callers
	 * that don't pass a value.
	 */
	delay?: number;
}

export const Tooltip: React.FC<TooltipProps> = ({
	children,
	content,
	position = 'auto',
	align = 'auto',
	showArrow = true,
	className,
	contentClassName,
	maxWidth = '400px',
	disabled = false,
	visible: controlledVisible,
	onVisibilityChange,
	portal = true,
	zIndex = Z_INDEX.tooltip,
	delay = 0,
}) => {
	const colors = useThemeColors();
	const [isVisible, setIsVisible] = useState(false);
	const [computedPosition, setComputedPosition] = useState<
		'top' | 'bottom' | 'left' | 'right'
	>('top');
	const [computedAlign, setComputedAlign] = useState<
		'start' | 'center' | 'end'
	>('center');
	const [coords, setCoords] = useState({ top: 0, left: 0 });

	const triggerRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	// Tracks the pending show-timer so mouseleave can cancel it before it fires.
	const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const visible =
		controlledVisible !== undefined ? controlledVisible : isVisible;

	const clearShowTimer = () => {
		if (showTimerRef.current != null) {
			clearTimeout(showTimerRef.current);
			showTimerRef.current = null;
		}
	};

	// Cancel any pending show-timer if the component unmounts while the user
	// is still hovering — otherwise the setState fires on a stale instance.
	useEffect(() => () => clearShowTimer(), []);

	const handleMouseEnter = () => {
		if (disabled) return;
		if (delay <= 0) {
			setIsVisible(true);
			onVisibilityChange?.(true);
			return;
		}
		clearShowTimer();
		showTimerRef.current = setTimeout(() => {
			setIsVisible(true);
			onVisibilityChange?.(true);
			showTimerRef.current = null;
		}, delay);
	};

	const handleMouseLeave = () => {
		if (disabled) return;
		clearShowTimer();
		setIsVisible(false);
		onVisibilityChange?.(false);
	};

	const handleClick = () => {
		if (disabled) return;
		clearShowTimer();
		const newVisible = !isVisible;
		setIsVisible(newVisible);
		onVisibilityChange?.(newVisible);
	};

	// Calculate position and alignment
	useEffect(() => {
		if (!visible || !triggerRef.current || !tooltipRef.current) return;

		const trigger = triggerRef.current.getBoundingClientRect();
		const tooltip = tooltipRef.current;
		const tooltipRect = tooltip.getBoundingClientRect();

		const padding = 8; // Distance from trigger
		const edgePadding = 12; // Distance from viewport edge
		const viewport = {
			width: window.innerWidth,
			height: window.innerHeight,
		};

		// Determine position
		let finalPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

		if (position === 'auto') {
			// Check available space in each direction
			const spaceAbove = trigger.top - edgePadding;
			const spaceBelow = viewport.height - trigger.bottom - edgePadding;
			const spaceLeft = trigger.left - edgePadding;
			const spaceRight = viewport.width - trigger.right - edgePadding;

			// Prefer top/bottom over left/right
			if (spaceAbove >= tooltipRect.height + padding) {
				finalPosition = 'top';
			} else if (spaceBelow >= tooltipRect.height + padding) {
				finalPosition = 'bottom';
			} else if (spaceLeft >= tooltipRect.width + padding) {
				finalPosition = 'left';
			} else if (spaceRight >= tooltipRect.width + padding) {
				finalPosition = 'right';
			} else {
				finalPosition = 'top'; // Fallback
			}
		} else {
			finalPosition = position as any;
		}

		setComputedPosition(finalPosition);

		// Calculate coordinates based on position
		let top = 0;
		let left = 0;
		let finalAlign: 'start' | 'center' | 'end' = 'center';

		if (finalPosition === 'top' || finalPosition === 'bottom') {
			// Vertical positioning
			top =
				finalPosition === 'top'
					? trigger.top - tooltipRect.height - padding
					: trigger.bottom + padding;

			// Horizontal alignment
			if (align === 'auto') {
				const centerLeft =
					trigger.left + trigger.width / 2 - tooltipRect.width / 2;

				if (centerLeft < edgePadding) {
					// Too close to left edge
					left = trigger.left;
					finalAlign = 'start';
				} else if (
					centerLeft + tooltipRect.width >
					viewport.width - edgePadding
				) {
					// Too close to right edge
					left = trigger.right - tooltipRect.width;
					finalAlign = 'end';
				} else {
					// Center is fine
					left = centerLeft;
					finalAlign = 'center';
				}
			} else if (align === 'start') {
				left = trigger.left;
				finalAlign = 'start';
			} else if (align === 'end') {
				left = trigger.right - tooltipRect.width;
				finalAlign = 'end';
			} else {
				left = trigger.left + trigger.width / 2 - tooltipRect.width / 2;
				finalAlign = 'center';
			}
		} else {
			// Horizontal positioning
			left =
				finalPosition === 'left'
					? trigger.left - tooltipRect.width - padding
					: trigger.right + padding;

			// Vertical alignment
			if (align === 'auto') {
				const centerTop =
					trigger.top + trigger.height / 2 - tooltipRect.height / 2;

				if (centerTop < edgePadding) {
					// Too close to top edge
					top = trigger.top;
					finalAlign = 'start';
				} else if (
					centerTop + tooltipRect.height >
					viewport.height - edgePadding
				) {
					// Too close to bottom edge
					top = trigger.bottom - tooltipRect.height;
					finalAlign = 'end';
				} else {
					// Center is fine
					top = centerTop;
					finalAlign = 'center';
				}
			} else if (align === 'start') {
				top = trigger.top;
				finalAlign = 'start';
			} else if (align === 'end') {
				top = trigger.bottom - tooltipRect.height;
				finalAlign = 'end';
			} else {
				top = trigger.top + trigger.height / 2 - tooltipRect.height / 2;
				finalAlign = 'center';
			}
		}

		// Ensure tooltip stays within viewport
		left = Math.max(
			edgePadding,
			Math.min(left, viewport.width - tooltipRect.width - edgePadding),
		);
		top = Math.max(
			edgePadding,
			Math.min(top, viewport.height - tooltipRect.height - edgePadding),
		);

		setCoords({ top, left });
		setComputedAlign(finalAlign);
	}, [visible, position, align]);

	// Arrow classes based on position and alignment
	const getArrowClasses = () => {
		const base = 'absolute w-0 h-0 border-4 border-transparent';

		if (computedPosition === 'top') {
			const vertical = 'top-full border-t-gray-800 dark:border-t-gray-700';
			if (computedAlign === 'start') return cn(base, vertical, 'left-4');
			if (computedAlign === 'end') return cn(base, vertical, 'right-4');
			return cn(base, vertical, 'left-1/2 -translate-x-1/2');
		}

		if (computedPosition === 'bottom') {
			const vertical = 'bottom-full border-b-gray-800 dark:border-b-gray-700';
			if (computedAlign === 'start') return cn(base, vertical, 'left-4');
			if (computedAlign === 'end') return cn(base, vertical, 'right-4');
			return cn(base, vertical, 'left-1/2 -translate-x-1/2');
		}

		if (computedPosition === 'left') {
			const horizontal = 'left-full border-l-gray-800 dark:border-l-gray-700';
			if (computedAlign === 'start') return cn(base, horizontal, 'top-2');
			if (computedAlign === 'end') return cn(base, horizontal, 'bottom-2');
			return cn(base, horizontal, 'top-1/2 -translate-y-1/2');
		}

		if (computedPosition === 'right') {
			const horizontal = 'right-full border-r-gray-800 dark:border-r-gray-700';
			if (computedAlign === 'start') return cn(base, horizontal, 'top-2');
			if (computedAlign === 'end') return cn(base, horizontal, 'bottom-2');
			return cn(base, horizontal, 'top-1/2 -translate-y-1/2');
		}

		return base;
	};

	const tooltipContent = (
		<div
			ref={tooltipRef}
			className={cn(
				'fixed px-3 py-2 rounded-md text-xs',
				colors.classes.tooltipBackground ?? 'bg-gray-800 dark:bg-gray-700',
				colors.classes.tooltipText ?? 'text-white',
				colors.classes.tooltipShadow ?? 'shadow-2xl',
				'transition-opacity duration-200',
				visible ? 'opacity-100' : 'opacity-0 pointer-events-none',
				contentClassName,
			)}
			style={{
				top: coords.top,
				left: coords.left,
				zIndex,
				maxWidth,
				width: 'max-content',
			}}
		>
			{content}
			{showArrow && <div className={getArrowClasses()} />}
		</div>
	);

	return (
		<>
			<div
				ref={triggerRef}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onClick={handleClick}
				className={className}
			>
				{children}
			</div>
			{visible &&
				(portal && typeof document !== 'undefined'
					? createPortal(tooltipContent, document.body)
					: tooltipContent)}
		</>
	);
};
